import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Bell, Mail, MessageSquare, CheckCircle, Car, Gift } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    rideReminders: true,
    driverAssigned: true,
    rideCompleted: true,
    promotions: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/auth/notifications/preferences');
      setPreferences(response.data.preferences);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/auth/notifications/preferences', preferences);
      
      toast({
        title: 'Success',
        description: 'Notification preferences saved',
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </Card>
    );
  }

  const notificationItems = [
    {
      key: 'emailNotifications',
      icon: Mail,
      title: 'Email Notifications',
      description: 'Receive notifications via email'
    },
    {
      key: 'smsNotifications',
      icon: MessageSquare,
      title: 'SMS Notifications',
      description: 'Receive notifications via text message'
    },
    {
      key: 'rideReminders',
      icon: Bell,
      title: 'Scheduled Ride Reminders',
      description: 'Get notified 1 hour before your scheduled rides'
    },
    {
      key: 'driverAssigned',
      icon: Car,
      title: 'Driver Assigned',
      description: 'Get notified when a driver accepts your ride'
    },
    {
      key: 'rideCompleted',
      icon: CheckCircle,
      title: 'Ride Completed',
      description: 'Get notified when your ride is completed'
    },
    {
      key: 'promotions',
      icon: Gift,
      title: 'Promotions & Offers',
      description: 'Receive special offers and promotional updates'
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Notification Preferences</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage how you receive notifications
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {notificationItems.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.key}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="h-5 w-5 text-pink-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
                </div>
              </div>
              <Switch
                checked={preferences[item.key]}
                onCheckedChange={() => handleToggle(item.key)}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-pink-600 hover:bg-pink-700 w-full sm:w-auto"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Email notifications are currently in test mode. 
          You can view notification logs in the console for testing purposes.
        </p>
      </div>
    </Card>
  );
};

export default NotificationPreferences;
